import React from "react"
import { useIntl } from "react-intl"
import { IntlShape } from "react-intl/lib"
import { ExportCSV } from "components/Shared/ExportCSV"
import { titleCapitalize } from "helpers/text"
import { NON_OPTION } from "../../constants"
import "./index.scss"

interface ExportProps {
	data: Array<DataProps>
	metric: string
	otherMetric: string
	interval: string
}

interface DataProps {
	primaryTrack: number
	secondaryTrack: number
	interval: number
	primaryDate: string
	secondaryDate: string
	detailTrack: number
	primaryTrack_?: number
	secondaryTrack_?: number
	detailTrack_?: number
}

const MESSAGE_IDS = {
	metricsOptions:
		"phronesis.transactional.application.commercial.benchmark.timeline.metricsOptions",
	filenameColumns: "phronesis.timeline.export.filename.columns",
	filename: "phronesis.timeline.export.filename",
}

const getDefaultTitle = (
	metric: string,
	secondMetric?: string,
	interval?: string
) => {
	let title = `benchmark_${metric}`
	if (secondMetric) {
		title += `_vs_${secondMetric}`
	}
	if (interval) {
		title += `_${interval}`
	}
	return title
}

const titleMetrics = (metric: string, intl: IntlShape) => {
	const formattedMetric = intl.formatMessage(
		{ id: MESSAGE_IDS.metricsOptions, defaultMessage: metric },
		{ metric }
	)
	return titleCapitalize(formattedMetric).replace(/\s/g, "")
}

const formatColumn = ({
	column,
	metric,
	intl,
}: {
	column: string
	metric?: string
	intl: IntlShape
}) =>
	intl.formatMessage({ id: MESSAGE_IDS.filenameColumns }, { column, metric })

const mapColumnsData = (
	data: Array<DataProps>,
	primaryMetric: string,
	secondaryMetric: string,
	intl: IntlShape
): Array<object> =>
	data.map((obj) => {
		const object = {
			[formatColumn({ column: "interval", intl })]: obj.interval,
			[formatColumn({ column: "primaryDate", intl })]: obj.primaryDate,
			[formatColumn({ column: "secondaryDate", intl })]: obj.secondaryDate,
			[formatColumn({
				column: "primaryTrack",
				metric: titleMetrics(primaryMetric, intl),
				intl,
			})]: obj.primaryTrack,
			[formatColumn({
				column: "secondaryTrack",
				metric: titleMetrics(primaryMetric, intl),
				intl,
			})]: obj.secondaryTrack,
			[formatColumn({
				column: "detailTrack",
				metric: titleMetrics(primaryMetric, intl),
				intl,
			})]: obj.detailTrack,
		}
		if (secondaryMetric !== NON_OPTION) {
			return {
				...object,
				[formatColumn({
					column: "primaryTrack_",
					metric: titleMetrics(secondaryMetric, intl),
					intl,
				})]: obj.primaryTrack_,
				[formatColumn({
					column: "secondaryTrack_",
					metric: titleMetrics(secondaryMetric, intl),
					intl,
				})]: obj.secondaryTrack_,
				[formatColumn({
					column: "detailTrack_",
					metric: titleMetrics(secondaryMetric, intl),
					intl,
				})]: obj.detailTrack_,
			}
		}
		return object
	})

const Export: React.FC<ExportProps> = ({
	data,
	metric,
	otherMetric,
	interval,
}) => {
	const intl = useIntl()
	return (
		<ExportCSV
			fileName={intl.formatMessage(
				{
					id: MESSAGE_IDS.filename,
					defaultMessage: getDefaultTitle(metric, otherMetric, interval),
				},
				{ metric, otherMetric, interval }
			)}
			data={mapColumnsData(data, metric, otherMetric, intl)}
		/>
	)
}

export default Export
